import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { startModifyForm } from '../../redux/actions/form';
import { Grid, Box, Typography, Button } from '@material-ui/core';

import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Radiolist } from '../../components/inputs/Radiolist';
import { LanguageInput } from '../../components/inputs/LanguageInput';
import { useLanguage } from "../../hooks/useLanguage";

import { SERVICE_TYPE_OPTIONS } from '../../enums/ServiceTypeOptions';
import { ToggleOption } from '../../components/inputs/ToggleOption';

import SafeIntroAr from '../../assets/images/survey/safe-intro/safe7-intro-ar.png'
import SafeIntroBn from '../../assets/images/survey/safe-intro/safe7-intro-bn.png'
import SafeIntroFa from '../../assets/images/survey/safe-intro/safe7-intro-fa.png'
import SafeIntroHi from '../../assets/images/survey/safe-intro/safe7-intro-hi.png'
import SafeIntroPa from '../../assets/images/survey/safe-intro/safe7-intro-pa.png'
import SafeIntroPl from '../../assets/images/survey/safe-intro/safe7-intro-pl.png'
import SafeIntroRo from '../../assets/images/survey/safe-intro/safe7-intro-ro.png'
import SafeIntroUr from '../../assets/images/survey/safe-intro/safe7-intro-ur.png'
import SafeIntroZh from '../../assets/images/survey/safe-intro/safe7-intro-zh-hant.png'
import SafeIntroEn from '../../assets/images/safe7-intro.png'

export const StartForm = () => {
  const form = useSelector((state) => state.form[0]);
  const { t, selectedLanguage } = useLanguage();
  const dispatch = useDispatch();

  useEffect(() => {
    const urlArr = window.location.href.split('/');
    const key = urlArr[4];
    if (key && key !== 'anonymous') {
      dispatch(startModifyForm({ ...form, key: key }));
    }
  }, []);


  const getIntroImage = () => {
    switch(selectedLanguage) {
      case 'en':
        return SafeIntroEn;
      case 'ar':
        return SafeIntroAr;
      case 'bn':
        return SafeIntroBn;
      case 'zh-hant':
        return SafeIntroZh;
      case 'fa':
        return SafeIntroFa;
      case 'hi':
        return SafeIntroHi;
      case 'pl':
        return SafeIntroPl;
      case 'pa':
        return SafeIntroPa;
      case 'ro':
        return SafeIntroRo;
      case 'ur':
        return SafeIntroUr;
      default:
        return SafeIntroEn;
    }
  };

  // Create translated options with fallback to original names in English
  const getTranslatedServiceOptions = () => {
    return SERVICE_TYPE_OPTIONS.map(option => {
      const translatedName = t(option.value);
      const isUntranslated = translatedName === option.value || !translatedName;

      return {
        ...option,
        name: isUntranslated ? option.name : translatedName
      };
    });
  };
  const translatedOptions = getTranslatedServiceOptions();

  let urlArr = window.location.href.split('/');
  const key = urlArr[4];
  const isAnonymous = key === 'anonymous';
  const ANONYMOUS_TYPE_OPTIONS = translatedOptions.filter(option => !option.name.includes('Care of the Dying'));
  let serviceOptions = []

  if (isAnonymous) {
    serviceOptions = ANONYMOUS_TYPE_OPTIONS;
  } else if (form?.allowServiceTypeSelect) {
    serviceOptions = translatedOptions;
  } else {
    const serviceTypeValue = form.serviceType;
    serviceOptions = translatedOptions.filter(option => option.value === serviceTypeValue);
  }

  const CarerOptions = [
    {
      name: t('start_radio_patient'),
      value: 'patient',
    },
    {
      name: t('start_radio_carer'),
      value: 'other',
    },
  ];

  const handleIsCarerSelect = (value) => {
    dispatch(startModifyForm({ ...form, isCarer: value }));
  };

  const startSurvey = () => {
    setTimeout(() => window.scrollTo(0, 0), 100);
    dispatch(startModifyForm({ ...form, formState: 'info' }));
  };

  const [validation, setValidation] = useState(false);
  const validate = () => {
    setValidation(true);
  };

  const [serviceTypeValidation, setServiceTypeValidation] = useState(false);
  const handleServiceTypeValidate = () => {
    setServiceTypeValidation(true);
  };

  const handleServiceTypeSelect = (serviceType) => {
      dispatch(startModifyForm({ ...form, serviceType: serviceType }));
      handleServiceTypeValidate();
  };

  const canSubmit = useMemo(() => !!form.isCarer && !!form.serviceType, [form.isCarer, form.serviceType]);

  const LTR = selectedLanguage === 'ar' || selectedLanguage === 'ur' || selectedLanguage === 'fa';

  return (
      <Grid container>
        <Grid item xs={12}>
          <LanguageInput />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display={'flex'} justifyContent={'center'}>
            <div style={{
              backgroundImage: `url(${getIntroImage()})`,
            }} className="intro-graphic"></div>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box my={4}>
            <Typography variant="h4" className="blue-font">
              <strong>{t('start_value_opinion')}</strong>
            </Typography>
          </Box>
          <Box my={2}>
            <Typography variant="subtitle1" className="blue-font">
              {t('start_intro_one')}
              <br />
              <br />
              {t('start_intro_two')}
              <br />
              <i>{t('start_intro_three')}</i>
            </Typography>
          </Box>
          <Radiolist
              options={CarerOptions}
              selected={form.isCarer}
              isRows={false}
              handleSelect={handleIsCarerSelect}
              validation={validation}
          />
          <Box my={2}>
            <Typography variant="h6" className="blue-font">
              {t('start_service_selection')}
            </Typography>
            <ToggleOption
                validationIndicatorMoreRight={true}
                options={serviceOptions}
                handleSelect={handleServiceTypeSelect}
                selected={form.serviceType}
                validation={serviceTypeValidation}
                twoButtonRow={true}
            />
          </Box>

          <Box my={2}>
            <Button
                endIcon={<ChevronRightIcon
                style={{
                  marginRight: LTR ? '10px' : '0px',
                  transform: LTR ? 'rotate(180deg)' : 'none',
                }}
                fontSize="large"
                />}
                className={`start-survey-btn ${canSubmit ? '' : 'inactive'}`}
                variant={'outlined'}
                onClick={canSubmit ? startSurvey : validate}
            >
              {t('start_button_text')}
            </Button>
          </Box>
        </Grid>
      </Grid>
  );
};
